import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { saveContractPdf, type ContractPdf } from "@app/api/contract-client";
import { CopyLandingPageButton } from "@app/components/copy-landing-page-button";
import { Spinner, SpinnerPage } from "@app/components/spinner";
import { EventInReviewPage } from "@app/pages/event-in-review/event-in-review-page";
import {
  DOWNLOAD_EVENT_CONTRACT_PDF,
  GET_ORGANIZER_EVENT,
  GET_EVENT_AGREEMENT,
  GET_EVENT_BOOKING_MANIFEST,
  GET_EVENT_REJECTION,
} from "@app/graphql/events.queries";
import {
  useEventBookingManifestPage,
  type BookingManifestEntry,
} from "@app/hooks/use-event-booking-manifest-page";
import { useOrganizationId } from "@app/hooks/use-organization-id";
import {
  ArrowLeftIcon,
  DownloadIcon,
  MaterialCalendarIcon,
  MaterialLocationOnIcon,
} from "@app/lib/events-admin-icons";
import { useNotifications } from "@app/lib/ui-foundation";
import {
  escapeCsvValue,
  formatDateRange,
  formatShortDate,
  type InviteStatus,
} from "@app/lib/events-admin-utils";
import {
  canOrganizerAccessSignedAgreement,
  getEventStatusDisplay,
  getEventStatusToneClass,
  isDeniedEventStatus,
  isEventInReview,
  normalizeEventStatus,
} from "@app/lib/event-status";
import { Link, useNavigate, useParams } from "react-router";
import {
  Button,
  PaginationPageDefault,
} from "@its-engineering-and-development/ui-foundation/components";
import "./event-detail-page.css";

const MANIFEST_PAGE_SIZE = 10;
const MANIFEST_EXPORT_LIMIT = 5000;
const AGREEMENT_DOWNLOAD_ERROR =
  "Unable to download the travel agreement. Please try again.";

type InviteTableStatus = InviteStatus | "DECLINED";

const inviteStatusMeta: Record<
  InviteTableStatus,
  {
    tableLabel: string;
    tone: "neutral" | "info" | "success" | "warning" | "critical";
  }
> = {
  SENT: { tableLabel: "SENT", tone: "neutral" },
  OPENED: { tableLabel: "OPENED", tone: "warning" },
  ACCEPTED: { tableLabel: "ACCEPTED", tone: "info" },
  BOOKED: { tableLabel: "BOOKED", tone: "success" },
  DECLINED: {
    tableLabel: "DECLINED",
    tone: "critical",
  },
};

function formatRecentActivity(isoDate: string | null | undefined) {
  if (!isoDate) {
    return "No recent activity";
  }

  const timestamp = new Date(isoDate).getTime();
  if (Number.isNaN(timestamp)) {
    return formatShortDate(isoDate);
  }

  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.max(0, Math.round(diffMs / 60000));

  if (diffMinutes < 2) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatShortDate(isoDate);
}

function normalizeInviteStatus(
  status: string | null | undefined,
): InviteTableStatus {
  switch ((status ?? "").toUpperCase()) {
    case "SENT":
    case "PENDING":
      return "SENT";
    case "OPENED":
    case "DELIVERED":
    case "TENTATIVE":
      return "OPENED";
    case "ACCEPTED":
      return "ACCEPTED";
    case "BOOKED":
    case "CONFIRMED":
    case "PURCHASED":
    case "TICKETED":
      return "BOOKED";
    case "DECLINED":
    case "REVOKED":
      return "DECLINED";
    default:
      return "SENT";
  }
}

function formatManifestSecondaryLine(traveler: BookingManifestEntry) {
  if (traveler.pnr) {
    return `Locator ${traveler.pnr}`;
  }

  if (traveler.flightNumber) {
    return `Flight ${traveler.flightNumber}`;
  }

  if (traveler.manifestPassengerId) {
    return `Passenger ${traveler.manifestPassengerId}`;
  }

  return "Booking manifest traveler";
}

interface GetEventQueryData {
  getEvent: {
    eventId: string;
    eventName: string;
    destination: string;
    venue: string;
    venueAddress: string;
    startDate: string;
    endDate: string;
    origin: string;
    status: string;
  } | null;
}

interface GetEventQueryVariables {
  eventId: string;
  organizationId: string;
}

interface GetEventBookingManifestData {
  getEventBookingManifest: {
    items: BookingManifestEntry[];
  } | null;
}

interface GetEventAgreementData {
  getEventAgreement: {
    status: string;
    signedContractId: string | null;
    acceptedAt: string | null;
  } | null;
}

interface GetEventRejectionData {
  getEvent: {
    rejectReason: string | null;
  } | null;
}

interface DownloadEventContractPdfData {
  downloadEventContractPdf: ContractPdf | null;
}

interface DownloadEventContractPdfVariables {
  eventId: string;
  organizationId: string;
}

type NormalizedEvent = {
  id: string;
  name: string;
  destination: string;
  venue: string;
  venueAddress: string;
  startDate: string;
  endDate: string;
  status: string;
  inviteLink: string;
};

export function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const apolloClient = useApolloClient();
  const { showError } = useNotifications();

  const { data: eventData, loading: isLoadingEvent } = useQuery<
    GetEventQueryData,
    GetEventQueryVariables
  >(GET_ORGANIZER_EVENT, {
    variables: { eventId: eventId ?? "", organizationId: organizationId ?? "" },
    skip: !eventId || !organizationId,
    fetchPolicy: "cache-and-network",
  });

  const hasEvent = Boolean(eventData?.getEvent);
  const eventStatus = eventData?.getEvent?.status ?? "";
  const normalizedEventStatus = normalizeEventStatus(eventStatus);
  const isInReviewEvent = isEventInReview(eventStatus);
  const isDeniedEvent = isDeniedEventStatus(eventStatus);
  const shouldLoadOperationalData =
    hasEvent && !isInReviewEvent && !isDeniedEvent;
  const canAccessSignedAgreement =
    canOrganizerAccessSignedAgreement(eventStatus);

  const {
    data: rejectionData,
    loading: isLoadingRejection,
    error: rejectionError,
  } = useQuery<GetEventRejectionData, GetEventQueryVariables>(
    GET_EVENT_REJECTION,
    {
      variables: {
        eventId: eventId ?? "",
        organizationId: organizationId ?? "",
      },
      skip: !eventId || !organizationId || !isDeniedEvent,
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
  );

  const { data: agreementData } = useQuery<
    GetEventAgreementData,
    GetEventQueryVariables
  >(GET_EVENT_AGREEMENT, {
    variables: {
      eventId: eventId ?? "",
      organizationId: organizationId ?? "",
    },
    skip: !eventId || !organizationId || !canAccessSignedAgreement,
    fetchPolicy: "cache-and-network",
  });

  const [downloadEventContractPdf, { loading: isDownloadingAgreement }] =
    useMutation<
      DownloadEventContractPdfData,
      DownloadEventContractPdfVariables
    >(DOWNLOAD_EVENT_CONTRACT_PDF);

  const {
    items: manifestItems,
    snapshotAt: manifestSnapshotAt,
    page: manifestPage,
    totalPages: manifestTotalPages,
    isLoading: isLoadingManifest,
    setPage: setManifestPage,
  } = useEventBookingManifestPage({
    eventId,
    pageSize: MANIFEST_PAGE_SIZE,
    enabled: shouldLoadOperationalData,
  });

  if (isLoadingEvent && !hasEvent) {
    return <SpinnerPage height="100dvh" />;
  }

  if (!hasEvent) {
    return (
      <section className="events-page">
        <div className="events-empty-state">
          <h1 className="events-page__title">Event not found</h1>
          <p className="events-page__subtitle">
            We couldn&apos;t find the requested event status page.
          </p>
          <Link to="/manage-events" className="events-inline-link">
            Back to events
          </Link>
        </div>
      </section>
    );
  }

  if (isInReviewEvent) {
    return <EventInReviewPage heading="Your event is in review" />;
  }

  const resolvedEventId = eventData?.getEvent?.eventId || eventId || "";

  const event: NormalizedEvent = {
    id: resolvedEventId,
    name: eventData?.getEvent?.eventName ?? "",
    destination: eventData?.getEvent?.destination ?? "",
    venue: eventData?.getEvent?.venue ?? "",
    venueAddress: eventData?.getEvent?.venueAddress ?? "",
    startDate: eventData?.getEvent?.startDate ?? "",
    endDate: eventData?.getEvent?.endDate ?? "",
    status: eventData?.getEvent?.status ?? "",
    inviteLink: `/events/${resolvedEventId}/landing-page`,
  };

  const denialReason = rejectionData?.getEvent?.rejectReason?.trim();
  const eventStatusDisplay = isDeniedEvent
    ? { label: "Denied", toneClass: "events-tone events-tone--critical" }
    : {
        label: getEventStatusDisplay(normalizedEventStatus ?? event.status)
          .label,
        toneClass: getEventStatusToneClass(
          normalizedEventStatus ?? event.status,
        ),
      };
  const agreement = agreementData?.getEventAgreement ?? null;
  const hasSignedAgreement =
    canAccessSignedAgreement &&
    agreement?.status === "SIGNED" &&
    Boolean(agreement.signedContractId);
  const agreementFilename = `Group_Travel_Agreement_${event.id.slice(-6)}.pdf`;

  const invitationRows = manifestItems.map((traveler, index) => ({
    id: traveler.bookingManifestId,
    name: traveler.passengerName || `Traveler ${index + 1}`,
    secondary: formatManifestSecondaryLine(traveler),
    status: normalizeInviteStatus(traveler.bookingStatus),
    recentActivity: formatRecentActivity(
      traveler.updatedAt ?? traveler.snapshotAt ?? manifestSnapshotAt,
    ),
  }));

  const inviteeCount = invitationRows.length;

  const displayVenue = event.venue.trim();
  const displayVenueAddress =
    event.venueAddress.trim() || event.destination.trim();
  const displayLocation = [displayVenue, displayVenueAddress]
    .filter(Boolean)
    .join(", ");
  const invitationSummary =
    inviteeCount > 0
      ? `Showing ${inviteeCount} traveler${inviteeCount === 1 ? "" : "s"} currently available from the booking manifest.`
      : "No travelers are available in the booking manifest yet.";

  const handleDownloadManifest = async () => {
    try {
      const { data } = await apolloClient.query<GetEventBookingManifestData>({
        query: GET_EVENT_BOOKING_MANIFEST,
        variables: {
          eventId: resolvedEventId,
          limit: MANIFEST_EXPORT_LIMIT,
        },
        fetchPolicy: "network-only",
      });

      const exportItems = data?.getEventBookingManifest?.items ?? manifestItems;
      const csvRows = [
        [
          "Passenger Name",
          "Passenger ID",
          "Locator",
          "Flight Number",
          "Booking Status",
          "Amount Paid",
          "Currency",
          "Last Updated",
        ].join(","),
        ...exportItems.map((traveler) =>
          [
            escapeCsvValue(traveler.passengerName ?? ""),
            escapeCsvValue(traveler.manifestPassengerId ?? ""),
            escapeCsvValue(traveler.pnr ?? ""),
            escapeCsvValue(traveler.flightNumber ?? ""),
            escapeCsvValue(traveler.bookingStatus ?? ""),
            escapeCsvValue(
              traveler.amountPaid == null ? "" : String(traveler.amountPaid),
            ),
            escapeCsvValue(traveler.currency ?? ""),
            escapeCsvValue(
              traveler.updatedAt ??
                traveler.snapshotAt ??
                manifestSnapshotAt ??
                "",
            ),
          ].join(","),
        ),
      ];

      const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${resolvedEventId}-booking-manifest.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showError(
        "Manifest download failed",
        "Unable to download the booking manifest right now.",
      );
    }
  };

  const handleDownloadAgreement = async () => {
    if (!organizationId || !hasSignedAgreement) {
      return;
    }

    try {
      const result = await downloadEventContractPdf({
        variables: {
          eventId: event.id,
          organizationId,
        },
      });
      const pdf = result.data?.downloadEventContractPdf;
      if (!pdf) {
        throw new Error(AGREEMENT_DOWNLOAD_ERROR);
      }

      saveContractPdf(pdf);
    } catch {
      showError("Agreement download failed", AGREEMENT_DOWNLOAD_ERROR);
    }
  };

  return (
    <section
      className={`events-page events-page--stack event-detail-page${isDeniedEvent ? " event-detail-page--denied" : ""}`}
    >
      <div className="event-detail-page__intro">
        <Link to="/manage-events" className="event-detail-page__back-link">
          <ArrowLeftIcon className="event-detail-page__back-icon" />
          Back to Events
        </Link>

        <div className="event-detail-page__hero-row">
          <header className="event-detail-page__hero-copy">
            <h1 className="events-page__title">{event.name}</h1>
            <div className="events-meta-row event-detail-page__meta-row">
              <span>
                <MaterialLocationOnIcon className="event-detail-page__meta-icon" />
                {displayLocation || "Location not set"}
              </span>
              <span
                className="event-detail-page__meta-separator"
                aria-hidden="true"
              />
              <span>
                <MaterialCalendarIcon className="event-detail-page__meta-icon" />
                {formatDateRange(event.startDate, event.endDate)}
              </span>
            </div>
            <div className="event-detail-page__status-row">
              <span className={eventStatusDisplay.toneClass}>
                {eventStatusDisplay.label}
              </span>
            </div>
          </header>

          {!isDeniedEvent ? (
            <div className="event-detail-page__toolbar">
              <CopyLandingPageButton
                eventId={event.id}
                className="event-detail-page__copy-button"
                iconClassName="event-detail-page__copy-icon"
              />
              <Button
                onPress={() => navigate(`/events/${event.id}/edit`)}
                size="lg"
                color="primary"
                className="event-detail-page__button event-detail-page__button--solid"
              >
                Edit Event
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="event-detail-page__divider" />

      {isDeniedEvent ? (
        <section
          className="event-detail-page__denial-notice"
          aria-labelledby="event-denial-reason-heading"
        >
          <h2 id="event-denial-reason-heading">Reason for denial</h2>
          {isLoadingRejection ? (
            <Spinner size={24} label="Loading denial reason" />
          ) : (
            <p>
              {rejectionError
                ? "The denial reason is currently unavailable."
                : denialReason || "No reason was provided."}
            </p>
          )}
        </section>
      ) : null}

      {hasSignedAgreement ? (
        <section
          className="event-detail-page__agreement-section"
          aria-labelledby="signed-agreement-heading"
        >
          <header>
            <h2 id="signed-agreement-heading">Signed agreement</h2>
            <p>Your approved group travel agreement is ready.</p>
          </header>
          <div className="event-detail-page__agreement-document">
            <span
              aria-hidden="true"
              className="event-detail-page__agreement-file-icon"
            >
              PDF
            </span>
            <div className="event-detail-page__agreement-file-copy">
              <strong>{agreementFilename}</strong>
              {agreement.acceptedAt ? (
                <span>Accepted on {formatShortDate(agreement.acceptedAt)}</span>
              ) : null}
            </div>
            <Button
              size="sm"
              color="primary"
              className="event-detail-page__agreement-download"
              onPress={() => void handleDownloadAgreement()}
              isDisabled={isDownloadingAgreement}
            >
              <DownloadIcon className="event-detail-page__table-action-icon" />
              {isDownloadingAgreement ? "Downloading..." : "Download PDF"}
            </Button>
          </div>
        </section>
      ) : null}

      {!isDeniedEvent ? (
        <section className="event-detail-page__invitation-section">
          <div className="events-page__hero event-detail-page__section-header">
            <div>
              <h2 className="event-detail-page__section-title">
                Attendee List
              </h2>
              <p className="events-page__subtitle">{invitationSummary}</p>
            </div>
            <div className="event-detail-page__table-actions">
              <button
                type="button"
                className="event-detail-page__table-action"
                onClick={() => {
                  void handleDownloadManifest();
                }}
              >
                <DownloadIcon className="event-detail-page__table-action-icon" />
                Download Manifest
              </button>
            </div>
          </div>

          <article className="events-surface events-surface--table event-detail-page__table-surface">
            {isLoadingManifest && inviteeCount === 0 ? (
              <div className="event-detail-page__table-state">
                <Spinner />
              </div>
            ) : inviteeCount === 0 ? (
              <p className="events-page__subtitle event-detail-page__table-state">
                No travelers are available in the booking manifest yet.
              </p>
            ) : (
              <table className="events-table event-detail-page__invite-table">
                <thead>
                  <tr>
                    <th>Participant Detail</th>
                    <th>Tracking Status</th>
                    <th>Recent Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {invitationRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="event-detail-page__participant-detail">
                          <strong className="events-table__primary-link event-detail-page__participant-name">
                            {row.name}
                          </strong>
                          <div className="event-detail-page__participant-email">
                            {row.secondary}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`event-detail-page__tracking-status event-detail-page__tracking-status--${inviteStatusMeta[row.status].tone}`}
                        >
                          <span className="event-detail-page__tracking-dot" />
                          {inviteStatusMeta[row.status].tableLabel}
                        </span>
                      </td>
                      <td className="event-detail-page__recent-activity">
                        {row.recentActivity}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!isLoadingManifest && manifestTotalPages > 1 ? (
              <PaginationPageDefault
                page={manifestPage}
                total={manifestTotalPages}
                onPageChange={setManifestPage}
                className="event-detail-page__pagination"
              />
            ) : null}
          </article>
        </section>
      ) : null}
    </section>
  );
}
